import JSONEditorWrapper from '@buildwise/libs/common/JSONEditor/JSONEditorWrapper';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  Typography,
} from '@mui/material';
import CustomDialogTitle from 'components/modal/CustomDialogTitle';
import { ConfigType } from 'config/config';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import _ from 'lodash';
import AlertModal from 'components/utils/AlertModal';
import { hasJsonChanged } from 'utility/helpers';
import { JSONType } from '@buildwise/libs/common/JSONEditor/JSONEditorWrapper';
import { ReactComponent as ExpandDiagonalLineIcon } from 'assets/icons/ExpandDiagonalLineIcon.svg';
import { ReactComponent as CollapseDiagonalLineIcon } from 'assets/icons/CollapseDiagonalLineIcon.svg';

type EditConfigModalProps = {
  title?: string;
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  config: JSONType;
  onSave: (config: ConfigType) => void;
  loading: boolean;
};

function EditConfigModal({ title, open, setOpen, config, onSave, loading }: EditConfigModalProps) {
  const [isFullScreen, setFullScreen] = useState(false);
  const [json, setJson] = useState<JSONType>(config);
  const [isModified, setIsModified] = useState(false);
  const [openConfirmation, setOpenConfirmation] = useState(false);

  useEffect(() => {
    const hasChanges = hasJsonChanged(config, json);
    setIsModified(hasChanges);
  }, [json]);

  const closeModal = () => {
    setOpen(false);
  };
  const handleSave = () => {
    const updatesJson = json as ConfigType;
    onSave(updatesJson);
  };

  const handleClose = () => {
    if (isModified) {
      setOpenConfirmation(true);
    } else {
      closeModal();
    }
  };

  return (
    <>
      <Dialog
        open={open}
        fullWidth
        maxWidth="lg"
        fullScreen={isFullScreen}
        aria-labelledby="dialog-title"
        aria-describedby="dialog-description"
        sx={{ '& .MuiPaper-root': { height: '100%' } }}
      >
        <CustomDialogTitle onClose={handleClose} id="dialog-title" sx={{ py: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography
              sx={{
                fontSize: '24px',
                fontWeight: 700,
                color: 'black',
                textTransform: 'capitalize',
              }}
            >
              {title
                ? title
                : Object.keys(config || {}).length <= 1
                  ? 'Add Configuration'
                  : `Edit ${config?.configName?.replaceAll?.('_', ' ')?.toLowerCase()}`}
            </Typography>
            <IconButton
              onClick={() => setFullScreen((prevState) => !prevState)}
              sx={{ color: 'custom.black', mr: 4 }}
            >
              {isFullScreen ? (
                <CollapseDiagonalLineIcon width={24} />
              ) : (
                <ExpandDiagonalLineIcon width={24} />
              )}
            </IconButton>
          </Box>
        </CustomDialogTitle>
        <DialogContent dividers id="dialog-description" sx={{ padding: 0, height: '500px' }}>
          <JSONEditorWrapper initialJson={json} setJson={setJson} defaultMode="code" />
        </DialogContent>

        <DialogActions sx={{ py: 1 }}>
          <Button variant="text" onClick={handleClose}>
            Cancel
          </Button>
          <Button variant="contained" disabled={!isModified || loading} onClick={handleSave}>
            {loading ? <CircularProgress color="inherit" size={20} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
      {openConfirmation && (
        <AlertModal
          open={openConfirmation}
          setOpen={setOpenConfirmation}
          handleCancel={() => {
            closeModal();
          }}
          onSubmit={handleSave}
          loading={loading}
          title="Confirm"
          submitButtonName={'Save'}
          cancelButtonName="Discard"
          description={'Are you sure you want to discard your changes?'}
          discardActions
        />
      )}
    </>
  );
}

export default EditConfigModal;
